import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { FaqSection } from '../FaqSection';
import Image from 'next/image';
import { Input } from '@/components/ui/input';
import { search } from '@/services/gonggongjiekou';
import copy from 'copy-to-clipboard';
import { Check } from 'lucide-react';
import SuccessCases from '../SuccessCases';
import { useRouter } from 'next/navigation';
import Head from '@/components/commons/Head';
import NewLoading from '@/components/commons/NewLoading';
import LatestNews from '../LatestNews';
import FollowUs from '../FollowUs';

export const countryFlagList = [
  { enValue: 'Canada', value: '加拿大' },
  { enValue: 'UnitedStates', value: '美国' },
  { enValue: 'Turkey', value: '土耳其' },
  { enValue: 'Malta', value: '马耳他' },
  { enValue: 'HongKong', value: '中国香港' },
  { enValue: 'Japan', value: '日本' },
  { enValue: 'Singapore', value: '新加坡' },
  { enValue: 'Australia', value: '澳洲' },
];

// 成功案例数据
export const successCasesList = [
  {
    tag: '加拿大成功案例',
    title: '环境优美，福利完善，教育资源丰富，移民后全方面保障家庭生活',
    image: '/images/home/successCases1.png',
    image_1: '/images/home/successCases1_mobile.png',
    link: `/successful-cases?area=${encodeURIComponent('加拿大')}`,
  },
  {
    tag: '美国成功案例',
    title: '世界强国，就业机会多，子女教育领先，医疗科研条件全球顶尖',
    image: '/images/home/successCases2.png',
    image_1: '/images/home/successCases3_mobile.png',
    link: `/successful-cases?area=${encodeURIComponent('美国')}`,
  },
  {
    tag: '欧洲成功案例',
    title: '身份含金量高，自由通行欧盟，多国教育福利共享，生活品质优越',
    image: '/images/home/successCases3.png',
    image_1: '/images/home/successCases4_mobile.png',
    link: `/successful-cases?area=${encodeURIComponent('欧洲')}`,
  },
  {
    tag: '亚洲成功案例',
    title: '地理位置近，文化相同，生活融入快，身份便利商务与发展',
    image: '/images/home/successCases4.png',
    image_1: '/images/home/successCases5_mobile.png',
    link: `/successful-cases?area=${encodeURIComponent('亚洲')}`,
  },
  {
    tag: '岛国成功案例',
    title: '护照免签150+国，税务政策宽松，资产配置与身份双赢',
    image: '/images/home/successCases5.jpg',
    image_1: '/images/home/successCases6_mobile.png',
    link: `/successful-cases?area=${encodeURIComponent('加勒比岛国')}`,
  },
  {
    tag: '【加拿大留学】',
    title: '加拿大教育体系完善，文凭全球认可，留学就业一体化的最佳选择',
    image: '/images/home/successCases6.png',
    image_1: '/images/home/successCases2_mobile.png',
    link: `/successful-cases?area=${encodeURIComponent('加拿大留学')}`,
  },
  {
    tag: '【澳洲移民】成功案例',
    title: '社会稳定，医疗教育福利一流，自然环境优美，宜居宜业首选之地',
    image: '/images/home/successCases7.png',
    image_1: '/images/home/successCases7_mobile.png',
    link: `/successful-cases?area=${encodeURIComponent('澳洲')}`,
  },
];

const HomeContent = () => {
  const t = useTranslations();
  const currentLocale = useLocale();
  const router = useRouter();

  const [showSearchIcon, setShowSearchIcon] = useState<boolean>(true);
  const [h5SearchValue, setH5SearchValue] = useState<string>(''); // h5搜索框值
  const [showContactCustomerService, setShowContactCustomerService] = useState<boolean>(false);
  const [showQRCode, setShowQRCode] = useState<boolean>(false);
  const [showCopySuccess, setShowCopySuccess] = useState<boolean>(false); // 展示复制成功
  const [showH5QRCode, setShowH5QRCode] = useState<string | null>(null); // H5关注我们二维码显示
  const [showPhoneDialog, setShowPhoneDialog] = useState<boolean>(false); // 显示电话弹窗

  // 评价数据
  const commentList = [
    {
      image: '/images/home/comment1.jpg',
      avatar: '/images/home/head1.png',
      userName: 'Freq',
      content:
        '从刚来到加拿大就在这家办签证，续签等，沟通很顺畅，也很负责，就算读书期间因为选课问题签证出了问题，也帮我研究，写了解释信，很感激。',
      link: 'https://maps.app.goo.gl/DLGskJUjJwLUZkC88',
    },
    {
      image: '/images/home/comment2.jpg',
      avatar: '/images/home/head2.png',
      userName: 'Olivia Chen',
      content:
        '这家公司的移民顾问们都超专业！我的case其实有挺多疑难点的，之前咨询了很多家中介，都没能给我一个满意的解决方案，他们家真的是为顾客着想、从顾客角度出发在做事。强烈推荐！',
      link: 'https://maps.app.goo.gl/TrSucQr1YH2RA4rbA',
    },
    {
      image: '/images/home/comment3.jpg',
      avatar: '/images/home/head3.png',
      userName: 'Grace Xu',
      content:
        '非常良心的移民中介， 我是在国内的时候通过谷歌找到的这个公司。初期服务我的唐少杰态度非常好，专业知识也过硬，给我留下了很好的印象，所以我最终选了他们。 后期的服务团队也很专业。如今我已经成功登陆加拿大并且拿到了身份。 感谢星环团队。',
      link: 'https://maps.app.goo.gl/uskTwM1s48nuEwwL8',
    },
    {
      image: '/images/home/echowu_bg.jpeg',
      avatar: '/images/home/echowu.png',
      userName: 'Echo Wu',
      content:
        '作为星环移成功上岸的案例，我们全家深感幸运。 星环在每一个流程都给予我们明确的指导，并让我们的资料看起来更加专业，与移民规则更加契合。 登陆前有专业的指导，该办的各种手续也会明确告知； 中期有回访；后期有案例结束的跟进。 非常喜欢一路陪伴我们走过的顾问Angel 及团队。 感恩🙏…...',
      link: 'https://maps.app.goo.gl/DZvqNiArDGuu4uo47',
    },
    {
      image: '/images/home/mzz_bg.jpeg',
      avatar: '/images/home/mzz.png',
      userName: 'MZ Z',
      content:
        '说实话找移民公司真的是一个体力和脑力活儿😞很消耗精力。从决定给家人办理夫妻团聚移民后，我从各个途径了解和考察了大温多家移民中介，当我抱着咨询下又不亏的心态下遇到了顾问兼负责人Angel Men，她亲切、热情，耐心倾听和了解了我的情况；专业、沉稳、从容、回答了我所有担心、顾虑的问题，（我的申请因为特殊原因有些难度）在与Angel短短的专业对话中让我学到了很多相关知识，受益匪浅。她知识渊博、经验丰富，让我信赖，所以我毫不犹豫的选择签约了星环移民 Starring Immigration公司，并在当天递交了我整理出来的所有材料。Angel随后也迅速组建了团队并安排了文案助理Lucia与我对接。Lucia人美心善，非常细心，任何时候都及时回复我的信息，做事特别负责，尤其是对细节的关注完美无缺👏因为有这个高效、专业、认真、主动、周到的团队帮助和跟进，仅仅8个月的时间我的家人就成功顺利地获得了PR😊毫无疑问我的选择是正确的！在此由衷的感谢Angel带领下的各位团队人员🙏因为你们的相助，让我们实现了夫妻团聚的梦想！强烈推荐，如果有想申请移民的一定要找星环移民公司，服务好👍速度快👍业务精👍有他们的帮助，一定让你心想事成！',
      link: 'https://maps.app.goo.gl/7UZdxh9jzDAmyyMn9',
    },
    {
      image: '/images/home/roseVan_bg.jpeg',
      avatar: '/images/home/roseVan.png',
      userName: 'Rose Van',
      content:
        '2年前认识星环，无论是移民咨询还是活动合作，接触到的星环集团从老板到员工都是那么热情、亲切、细致、周到，深感星环的专业、大气，值得信赖👍…...',
      link: 'https://maps.app.goo.gl/pqZXoVHm5MpJZjqJ7',
    },
    {
      image: '/images/home/yuxia_bg.jpeg',
      avatar: '/images/home/yuxia.png',
      userName: 'Yu Xia',
      content:
        '我在星环移民公司度过了一段美妙的经历。他们在整个过程中提供了宝贵的支持和指导。他们的团队知识渊博、反应敏捷，并竭尽全力确保顺利过渡到新的国家。感谢他们的专业知识，我现在可以自信、安心地开启人生的新篇章。我强烈推荐他们的服务给任何寻求专业移民帮助的人。',
      link: 'https://maps.app.goo.gl/PTncGjzAMr8aNWhz6',
    },
    {
      image: '/images/home/xiaolibing_bg.jpeg',
      avatar: '/images/home/xiaolibing.png',
      userName: '肖礼斌',
      content: '星环移民非常靠谱，好几个校友在他家办理的移民留学，很专业也很负责，是移民行业的一股清流。推荐！',
      link: 'https://maps.app.goo.gl/igrpWXtanjDZKJbv5',
    },
  ];

  // 常见问题数据
  const faqList = [
    {
      title: 'Q1、如何选择适合自己的海外移民项目？',
      answer: [
        {
          type: 'description',
          content:
            '当对选择哪个国家移民不确定时，最好选择可以提供「全球身份规划」的公司和顾问团队，了解不同国家的优势与缺点，结合家庭情况做出最适合的选择：',
        },
        {
          type: 'list',
          content: (
            <span>
              <strong>明确家庭诉求：</strong>
              移民是为子女教育、还是跨境商务与资产配置？是全家移民，还是孩子单独移民？计划移居海外还是只需要一个国外身份？
            </span>
          ),
        },
        {
          type: 'list',
          content: (
            <span>
              <strong>先选国家，后选项目：</strong>
              世界这么大，选择适合自己的国家才有过上理想中的生活。客观了解不同国家的差异，结合教育、税务、职业等特点，以及居住要求等综合考量，选定国家，再确定适合的项目。{' '}
            </span>
          ),
        },
        {
          type: 'list',
          content: (
            <span>
              <strong>咨询专业顾问：</strong>
              市场上有些所谓的“顾问”自己都从未出国，对国外情况完全是道听途说。因此，要了解适合的海外移民项目，最好选择有海外生活经验、持该国专业顾问资质的移民或留学顾问，获得更真实、专业的针对性建议。{' '}
            </span>
          ),
        },
      ],
    },
    {
      title: 'Q2、办理移民需要满足哪些基本条件？',
      answer: [
        {
          type: 'description',
          content:
            '每个国家、不同移民项目的要求截然不同，有的对学历、工作经验、语言、财务状况等有具体要求，有的则没有相关要求。但一般都会有体检合格及无犯罪记录的基本要求。移民前务必要充分了解各项目的具体要求，选择适合家庭实际情况和需求的项目。',
        },
      ],
    },
    {
      title: 'Q3、从咨询到成功移民，整个流程需要多久的时间？',
      answer: [
        {
          type: 'description',
          content: '流程因国家和项目而异，大致分为：',
        },
        {
          type: 'list',
          content: (
            <span>
              <strong>咨询与评估：</strong>1-2周，明确需求与资格。
            </span>
          ),
        },
        {
          type: 'list',
          content: (
            <span>
              <strong>与「星环移民」签约：</strong>确定方案，签署合同。
            </span>
          ),
        },
        {
          type: 'list',
          content: (
            <span>
              <strong>准备材料：</strong>1-3个月，顾问提供清单指导。
            </span>
          ),
        },
        {
          type: 'list',
          content: (
            <span>
              <strong>递交申请与审理：</strong>时间因国家不同，可能数月到数年。
            </span>
          ),
        },
        {
          type: 'list',
          content: (
            <span>
              <strong>签证获批与登陆：</strong>顾问协助后续安置。
            </span>
          ),
        },
        {
          type: 'description',
          content: '「星环移民」全程跟进流程，确保客户每一步都高效顺利推进，大幅提升申请成功率。',
        },
      ],
    },
    {
      title: 'Q4、「星环移民」｜在移民和留学方面有什么优势？',
      answer: [
        {
          type: 'description',
          content: (
            <span>
              「星环移民」拥有多国持牌顾问和律师团队，10年以上行业经验，数千成功案例，
              <strong>成功率高达98%以上，</strong>在加拿大、中国、欧美、澳洲等地均有本地化团队与合作伙伴。
            </span>
          ),
        },
        {
          type: 'description',
          content:
            '我们擅长为客户定制高性价比的一站式方案，以客户需求为导向提供专业化建议，覆盖移民、留学、身份规划和跨境资产配置，服务范围遍及全球。',
        },
        {
          type: 'description',
          content:
            '10余年来，星环坚持“以诚为本，做行业清流”，是“BBB北美商业改进局”权威认证的A+级企业，Google 五星级评价企业，深受客户好评。',
        },
      ],
    },
    {
      title: 'Q5、办理移民和留学时，「星环移民」是否提供一对一的服务？',
      answer: [
        {
          type: 'description',
          content:
            '「星环移民」实行多对一团队、中英双语服务。每位客户都匹配专属持牌顾问、项目总监和文案，客户经理全程陪伴，项目总监督促服务质量，确保申请顺利、沟通顺畅，并在落地安家、学习生活和就业方面提供本地化支持。',
        },
      ],
    },
  ];

  const [searchLoading, setSearchLoading] = useState<boolean>(false); // 搜索loading
  const [moreLoading, setMoreLoading] = useState<boolean>(false); // 更多loading
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [hotWordsData, setHotWordsData] = useState<any[]>([]); // 搜索热词
  const [showHot, setShowHot] = useState<boolean>(false);
  const navBarInputRef = useRef<HTMLInputElement>(null);
  const moreTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 热词展开的时候，点击输入框和热词删除按钮都不收起热词框
    const handleCloseHotword = (e: any) => {
      if (
        e.target &&
        ((moreTextRef?.current && moreTextRef.current.contains(e.target as Node)) ||
          (navBarInputRef?.current && navBarInputRef.current.contains(e.target as Node)))
      ) {
        return;
      }
      setTimeout(() => {
        setShowHot(false);
      }, 100);
    };
    if (showHot) {
      document.body.addEventListener('click', handleCloseHotword);
    }

    return () => {
      if (showHot) {
        document.body.removeEventListener('click', handleCloseHotword);
      }
    };
  }, [showHot]);

  const cards = [
    {
      titleKey: '美国【EB1A&NIW杰出人才移民】', //美国【EB1A&NIW杰出人才移民】
      desc: (
        <span>
          The U.S Employment-Based Immigration <br /> Third Preference EB-1A
          <br /> & National interest Waiver (NlW)
        </span>
      ),
      contentKey: <span>一步到位拿绿卡 | 支持双申</span>,
      url: '/%e7%be%8e%e5%9b%bd%e6%9d%b0%e5%87%ba%e4%ba%ba%e6%89%8d%e7%a7%bb%e6%b0%91%e4%b8%8e%e5%9b%bd%e5%ae%b6%e5%88%a9%e7%9b%8a%e8%b1%81%e5%85%8d%e7%a7%bb%e6%b0%91%ef%bc%88eb1aniw%ef%bc%89/',
    },
    {
      titleKey: '澳洲【雇主担保移民】', //澳洲【雇主担保移民】
      desc: `Australia Employer Sponsored immigration`,
      contentKey: (
        <span>
          低门槛、快速移民“窗口期” ，<br />
          雅思5分 | 4个月获工签，
          <br />
          工作2年申请PR，
          <br />
          雅思6分 | 可直接申请PR
        </span>
      ),
      url: '/澳洲494偏远地区雇主担保移民/',
    },
    {
      titleKey: '葡萄牙【黄金签证】', // 葡萄牙黄金签证
      desc: 'Portuguese Golden Visa',
      contentKey: (
        <span>
          25万欧元高性价比|欧盟护照跳板
          <br />
          98% 入籍通过率 | 三代同享
        </span>
      ),
      url: '/%E8%91%A1%E8%90%84%E7%89%99%E9%BB%84%E9%87%91%E5%B1%85%E7%95%99%E8%AE%B8%E5%8F%AF%E9%A1%B9%E7%9B%AE/',
    },
    {
      titleKey: '日本【经营管理&高度人才签证】', //日本经营管理&高度人才签证
      desc: (
        <span>
          Japanese Business Manager Visa & <br />
          Highly Skilled Professional Visa
        </span>
      ),
      contentKey: (
        <span>
          低门槛获得“亚洲最发达国家”身份
          <br />
          华人融入无时差，生活质量优渥
        </span>
      ),
      url: '/%E6%97%A5%E6%9C%AC%E7%BB%8F%E8%90%A5%E7%AE%A1%E7%90%86%E7%AD%BE%E8%AF%81/',
    },
    {
      titleKey: '多米尼克【投资入籍计划】', //多米尼克投资入籍计划
      desc: (
        <span>
          Dominica Citizenship by <br />
          Investment Program
        </span>
      ),
      contentKey: (
        <span>
          最快6个月获批 | 先获批后捐款
          <br />
          英联邦护照 | 全球资产配置优选
        </span>
      ),
      url: '/%E5%A4%9A%E7%B1%B3%E5%B0%BC%E5%85%8B%E6%8A%95%E8%B5%84%E5%85%A5%E7%B1%8D%E8%AE%A1%E5%88%92/',
    },
    {
      titleKey: '希腊【黄金居留项目】', //希腊黄金居留项目
      desc: 'Greece Golden Visa Program',
      contentKey: (
        <span>
          欧洲发达国家
          <br />
          最后的“购房移民”项目
        </span>
      ),
      url: '/%E5%B8%8C%E8%85%8A%E9%BB%84%E9%87%91%E6%B0%B8%E5%B1%85%E9%A1%B9%E7%9B%AE/',
    },
    {
      titleKey: '马耳他【永居项目】', //马耳他永居项目
      desc: (
        <span>
          Malta Permanent
          <br />
          Residence Program
        </span>
      ),
      contentKey: (
        <span>
          欧洲发达国家
          <br />
          {`“成本最低”的绿卡项目`}
        </span>
      ),
      url: '/%E9%A9%AC%E8%80%B3%E4%BB%96%E6%B0%B8%E5%B1%85%E9%A1%B9%E7%9B%AE/',
    },
    {
      titleKey: '格林纳达【投资入籍计划】', //格林纳达投资入籍计划
      desc: (
        <span>
          Grenada Citizenship by
          <br />
          Investment Program
        </span>
      ),
      contentKey: (
        <span>
          {`一步到位拿护照 | "高净值人士"标配`}
          <br />
          先获批，后捐款 | 捐款方案简洁明了
        </span>
      ),
      url: '/%e6%a0%bc%e6%9e%97%e7%ba%b3%e8%be%be%e6%8a%95%e8%b5%84%e5%85%a5%e7%b1%8d%e8%ae%a1%e5%88%92/',
    },
  ];

  const BusinessCard = ({ titleKey, desc, contentKey, url }: (typeof cards)[number]) => (
    <div
      className="flex w-[19.084vw] cursor-pointer flex-col items-center justify-center rounded-[20px] bg-[#E6EAFF] px-[0.856vw] py-[1.768vw]"
      onClick={() => {
        if (!url) return;
        window.open(`${process.env.NEXT_PUBLIC_WEBSITE}${url}`, '_blank');
      }}
    >
      <h2 className="mb-[0.52vw] text-center text-[1.04vw] font-medium text-[#111111]">{titleKey}</h2>
      <h2 className="mb-[0.52vw] text-center text-[0.728vw] font-normal leading-[1.04vw] text-[#666666]">{desc}</h2>
      <h3 className="text-center text-[0.96vw] font-normal leading-tight text-[#111111] opacity-80">{contentKey}</h3>
    </div>
  );

  return (
    <div className="w-full">
      <Head
        title={'星环移民，移民顾问咨询、签证办理、学校申请'}
        description={
          '星环移民，专注加拿大移民和留学十余载，擅长量身定制方案，提供高性价比服务，成功率居行业领先水平。旗下拥有多位全职的ICCRC认证的「加拿大持牌移民顾问」、教育专家和职业培训导师，专业办理加拿大移民、留学、游学、夏令营、教育等服务，提供顾问咨询、签证办理、学校申请、就业指导、财税规划及新移民安家落地、置业创业等一条龙服务，对于解决各种签证、留学、移民疑难问题有丰富经验与成功案例。'
        }
        keywords="移民,海外移民,加拿大移民,全球移民,移民咨询,移民公司,移民中介,星环移民"
      />
      {/* Swipper */}
      <div className="homePageSwipper mb-[2.6vw] box-border w-full px-[100px] max-md:hidden">
        <Swiper
          className="homePageBannerSwiper"
          modules={[Pagination, Autoplay]}
          spaceBetween={50}
          slidesPerView={1}
          autoplay={{
            delay: 5000,
          }}
          loop
          pagination={{ clickable: true, el: '.homePageBannerSwiperPagination' }}
        >
          {[
            {
              title: '美国EB-3雇主担保移民',
              url: `/us-eb3`,
            },
            {
              title:
                '全球身份规划专家。专注于全球移民、留学、就业和安家等服务。提供学校申请、身份规划、签证办理、安家落地、就业与创业支持等。「全球化」「一站式」全程服务',
              url: `/about-us`,
            },
            {
              title:
                '加拿大移民。SUV 联邦创业移民、联邦护工（保姆）移民。联邦EE技术移民快速通道+LMIA、各省雇主担保移民。企业家移民、留学、各类工签办理',
              url: `/immigration?country=加拿大`,
            },
            {
              title: '美国移民。EB-5 投资移民。EB-1A 杰出人才移民、NIW 国家利益豁免移民',
              url: `/immigration?country=美国`,
            },
            {
              title: '欧洲移民。爱尔兰、土耳其、塞浦路斯、西班牙葡萄牙、马耳他、希腊',
              url: `/immigration?country=欧洲`,
            },
            {
              title: '亚洲移民。香港特别行政区、日本、新加坡，韩国、泰国、菲律宾',
              url: `/immigration?country=亚洲`,
            },
            {
              title: '加勒比和太平洋岛国。安提瓜和巴布达、圣卢西亚、瓦努阿图、多米尼克',
              url: `/immigration?country=加勒比`,
            },
          ].map((item, bannerIndex) => {
            return (
              <SwiperSlide key={bannerIndex}>
                <div
                  style={{
                    backgroundImage: `url(${`/images/home/banner${bannerIndex}${currentLocale === 'en' ? '_en' : ''}.${(bannerIndex === 0 || bannerIndex === 2 || bannerIndex === 6) && currentLocale === 'en' ? 'jpg' : 'png'}`})`,
                    paddingTop: `${(900 / 2580) * 100}%`, // 34.9%
                  }}
                  className="homePageBannerSwiperItem box-border w-full cursor-pointer bg-[#1A5C78] bg-cover bg-center bg-no-repeat"
                  onClick={() => {
                    if (!item?.url) return;
                    router.push(`${item?.url}`);
                  }}
                ></div>
              </SwiperSlide>
            );
          })}
          <div className="homePageBannerSwiperPagination mt-[1.768vw] flex flex-row items-center justify-center"></div>
        </Swiper>
      </div>
      {/* banner h5 */}
      <div
        style={{ backgroundImage: 'url(/images/home/bannerBg.png)' }}
        className="box-border h-[318px] w-full bg-cover bg-center bg-no-repeat pt-14 md:hidden"
      >
        <div className="mb-11 box-border px-[30px] text-lg font-medium text-white opacity-0">
          {t('Home.h5HomeBannerTitle')}
        </div>
        <div
          className={`relative mx-auto box-border flex h-[42px] w-[92%] flex-row items-center justify-between rounded-full bg-white pl-[17px] pr-1 ${!showSearchIcon || h5SearchValue ? 'opacity-100' : 'opacity-70'}`}
        >
          <div className="relative flex h-9 flex-1 flex-row items-center justify-start">
            {showSearchIcon && (
              <Image src={`/images/home/search.png`} width={35} height={35} className="size-[18px]" alt="search" />
            )}
            <Input
              value={h5SearchValue}
              className="absolute left-0 top-0 h-full w-full flex-1 border-none pl-0 text-[#111111]"
              onFocus={() => setShowSearchIcon(false)}
              onBlur={() => setShowSearchIcon(!h5SearchValue)}
              onChange={(e) => {
                const value = e.target.value;
                if (!value) {
                  setShowSearchIcon(true);
                } else {
                  setShowSearchIcon(false);
                }
                setH5SearchValue(e.target.value);
              }}
            />
          </div>
          <div
            className="flex h-[35px] w-[60px] flex-row items-center justify-center rounded-[35px] bg-[#FFAF3A] text-xs text-[#333333]"
            onClick={() => {
              // console.log(h5SearchValue);
              // if (activeTabKey === '移民') {
              //   router.push(`/immigration?keyword=${h5SearchValue}`);
              // }
              // if (activeTabKey === '留学') {
              //   router.push(`/study-abroad?keyword=${h5SearchValue}`);
              // }
              // if (activeTabKey === '签证') {
              //   router.push(`/visa?keyword=${h5SearchValue}`);
              // }
              if (!h5SearchValue.trim()) {
                return;
              }

              setSearchLoading(true);
              // 关键词提示
              search({
                search: h5SearchValue,
                page: 1,
                subtype: 'page,post',
              })
                .then((response) => {
                  setTotal(response.headers['x-wp-total'] || 0);
                  setPage(1);
                  setHotWordsData(response.data || []);
                  setShowHot(true);
                })
                .finally(() => {
                  setSearchLoading(false);
                });
            }}
          >
            {t('Home.search')}
          </div>

          {showHot ? (
            <div
              style={{
                boxShadow: '0px 7px 21px 3px rgba(42,42,42,0.17)',
              }}
              id="homeH5PageSearchResult"
              className={`absolute left-0 top-[48px] max-h-[220px] w-full overflow-y-auto rounded-xl bg-white p-[16px]`}
            >
              {searchLoading ? (
                <div className="flex flex-row items-center justify-center">
                  <NewLoading />
                </div>
              ) : hotWordsData && !!hotWordsData.length ? (
                <div>
                  {(hotWordsData ? (hotWordsData ?? []) : []).map((hotWordItem, index) => {
                    return (
                      <div
                        key={index}
                        className="mb-1 flex h-5 w-full cursor-pointer flex-row items-center justify-start last-of-type:mb-0"
                      >
                        <Link
                          href={hotWordItem.url}
                          target="_blank"
                          className={`flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap text-xs text-[#3A3A3A]`}
                          onClick={() => {
                            setShowHot(false);
                          }}
                        >
                          {hotWordItem.title}
                        </Link>
                      </div>
                    );
                  })}
                  {page * 10 < total && (
                    <div
                      ref={moreTextRef}
                      className="mt-2 flex w-full cursor-pointer flex-row items-center justify-center text-center text-xs text-[#3A3A3A]"
                      onClick={() => {
                        const newPage = page + 1;

                        setMoreLoading(true);
                        // 关键词提示
                        search({
                          search: h5SearchValue,
                          page: newPage,
                          subtype: 'page,post',
                        })
                          .then((response) => {
                            setTotal(response.headers['x-wp-total'] || 0);

                            setPage(newPage);
                            const newData = hotWordsData.concat(response.data || []);
                            setHotWordsData(newData);
                          })
                          .finally(() => {
                            setMoreLoading(false);
                          });
                      }}
                    >
                      {moreLoading ? <NewLoading size={'small'} /> : '显示更多'}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full text-center text-sm text-[#3A3A3A]">无相关内容</div>
              )}
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
      {/* 全球移民 h5 */}
      <div className="-mt-[114px] w-full rounded-t-3xl md:hidden">
        <div
          className="box-border w-full rounded-t-3xl px-[15px] pt-5"
          style={{ backgroundImage: 'linear-gradient(to bottom, #BEBFDD 0%, #FFFFFF 100%)' }}
        >
          <div className="mb-[22px] w-full text-lg font-semibold leading-[18px] text-[#111111]">
            {t('Home.hotImmigration')}
          </div>
          <div className="mb-9 grid w-full grid-cols-3 gap-x-2 gap-y-[10px]">
            {[
              {
                label: '加拿大【法语人才移民】',
                enLabel: `Canadian French-Language Proficiency under Express Entry`,
                link: '/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e3%80%90%e6%b3%95%e8%af%ad%e4%ba%ba%e6%89%8d%e7%a7%bb%e6%b0%91%e3%80%91/',
              },
              {
                label: '美国【EB3雇主担保移民】',
                enLabel: `The U.S.Employment-Based Immigration: Third Preference EB-3`,
                link: '/us-eb3',
              },
              {
                label: '加拿大【留学转移民】',
                enLabel: `Canadian Study Pathway to Permanent Residency Card`,
                link: '/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%a0%94%e7%a9%b6%e7%94%9f%e7%95%99%e5%ad%a6%e8%bd%ac%e7%a7%bb%e6%b0%91%e5%85%a8%e6%94%bb%e7%95%a5/',
              },
              {
                label: '加拿大【RCIP社区移民试点】',
                enLabel: `Canadian Rural Community Immigration Pilot`,
                link: '/加拿大阿省rrs偏远地区雇主担保移民/',
              },
              {
                label: '安提瓜和巴布达【公民身份计划】',
                enLabel: `Antigua and Barbuda Citizenship by Investment Programme`,
                link: 'https://posts.starringglobal.com/%e5%ae%89%e6%8f%90%e7%93%9c%e5%92%8c%e5%b7%b4%e5%b8%83%e8%be%be%e5%85%ac%e6%b0%91%e8%ba%ab%e4%bb%bd%e8%ae%a1%e5%88%92/',
              },
              {
                label: '爱尔兰【投资居留计划】',
                enLabel: `Irish Immigrant Investor Programme`,
                link: 'https://posts.starringglobal.com/%e7%88%b1%e5%b0%94%e5%85%b0%e6%8a%95%e8%b5%84%e5%b1%85%e7%95%99%e8%ae%a1%e5%88%92/',
              },
              {
                label: '美国【EB5投资移民】',
                enLabel: `The U.S.Employment-Based lmmigration: Third Preference EB-5`,
                link: '/美国eb5投资移民/',
              },
              {
                label: '圣基茨和尼维斯【国民身份计划】',
                enLabel: `Saint Kitts and Nevis Citizenship Program`,
                link: 'https://posts.starringglobal.com/%E5%9C%A3%E5%9F%BA%E8%8C%A8pbo%E6%8D%90%E6%AC%BE%E7%A7%BB%E6%B0%91%E9%A1%B9%E7%9B%AE/',
              },
            ].map((item, index) => {
              return (
                <div
                  key={index}
                  style={{
                    backgroundImage: `url(/images/home/bannerBg${(index % 6) + 1}.png)`,
                  }}
                  className="box-border flex flex-col items-start justify-start rounded-lg bg-[#e6eaff] bg-cover bg-center bg-no-repeat p-[9px]"
                  onClick={() => {
                    const link = item.link;
                    if (link) {
                      if (link === '/us-eb3') {
                        router.push(link);
                      } else if (link.startsWith('http') || link.startsWith('https')) {
                        window.open(link, '_blank');
                      } else {
                        window.open(`${process.env.NEXT_PUBLIC_WEBSITE}${link}`, '_blank');
                      }
                    }
                  }}
                >
                  <div className="mb-2 text-xs font-semibold tracking-tighter text-[#333333]">{item.label}</div>
                  <div className="text-[10px] leading-[12px] tracking-tighter text-[#666666]">{item.enLabel}</div>
                </div>
              );
            })}
            <div
              className="box-border flex min-h-16 items-center justify-center rounded-lg bg-[#e6eaff] p-[9px] text-xs text-[#333333]"
              onClick={() => router.push('/immigration')}
            >
              {t('Home.seeMore')}
            </div>
          </div>
          <div className="mb-[22px] w-full text-lg font-semibold leading-[18px] text-[#111111]">
            {t('Home.hotStudy')}
          </div>
          <div className="mb-9 grid w-full grid-cols-3 gap-x-2 gap-y-[10px]">
            {[
              {
                label: '加拿大中小学申请',
                enLabel: 'K-12 School Applications in Canada',
                link: '/school%20searching%20tool/elementory%20school%20and%20secondary%20school%20application/',
              },
              {
                label: '加拿大本硕博申请',
                enLabel: 'Undergraduate, Master, and PhD Applications in Canada',
                link: 'https://posts.starringglobal.com/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e6%9c%ac%e7%a7%91%e3%80%81%e7%a1%95%e5%a3%ab%e4%b8%8e%e5%8d%9a%e5%a3%ab%e7%94%b3%e8%af%b7%e5%ae%8c%e6%95%b4%e6%8c%87%e5%8d%97/',
              },
              {
                label: '藤校以及海外名校规划',
                enLabel: 'Top Global University Planning',
                link: 'https://posts.starringglobal.com/%e7%be%8e%e5%9b%bd%e8%97%a4%e6%a0%a1%e4%b8%8e%e6%b5%b7%e5%a4%96%e5%90%8d%e6%a0%a1%e7%94%b3%e8%af%b7%e6%8c%87%e5%8d%97/',
              },
            ].map((item, index) => {
              return (
                <div
                  key={index}
                  style={{
                    backgroundImage: `url(/images/home/bannerBg${(index % 6) + 1}.png)`,
                  }}
                  className="box-border flex flex-col items-start justify-start rounded-lg bg-[#e6eaff] bg-cover bg-center bg-no-repeat p-[9px]"
                  onClick={() => {
                    const link = item.link;
                    if (link) {
                      if (link.startsWith('http') || link.startsWith('https')) {
                        window.open(link, '_blank');
                      } else {
                        window.open(`${process.env.NEXT_PUBLIC_WEBSITE}${link}`, '_blank');
                      }
                    }
                  }}
                >
                  <div className="mb-2 text-xs font-semibold tracking-tighter text-[#333333]">{item.label}</div>
                  <div className="text-[10px] leading-[12px] tracking-tighter text-[#666666]">{item.enLabel}</div>
                </div>
              );
            })}
            <div
              className="box-border flex min-h-16 items-center justify-center rounded-lg bg-[#e6eaff] p-[9px] text-xs text-[#333333]"
              onClick={() => router.push('/study-abroad')}
            >
              {t('Home.seeMore')}
            </div>
          </div>
          <div className="mb-[22px] w-full text-lg font-semibold leading-[18px] text-[#111111]">
            {t('Home.hotVisa')}
          </div>
          <div className="mb-9 grid w-full grid-cols-3 gap-x-2 gap-y-[10px]">
            {[
              {
                label: '加拿大【旅游签证】',
                enLabel: 'Visitor Visa (Temporary Resident Visa, TRV)',
                link: '/visa/short%20term%20visa/visitor%20visa/',
              },
              {
                label: '加拿大【陪读签证】',
                enLabel: 'Visitor Record for Accompanying Family Member',
                link: '/study%20permit%20application%202/parents%20visa%20application%20and%20renew/',
              },
              {
                label: '加拿大【超级签证】',
                enLabel: 'Super Visa',
                link: '/visa/short%20term%20visa/super%20visa/',
              },
              {
                label: '加拿大【学习签证】',
                enLabel: 'Study Permit',
                link: '/学签申请/',
              },
              {
                label: '加拿大【毕业工签】',
                enLabel: 'Post-Graduation Work Permit (PGWP)',
                link: '/visa/student%20related%20work%20permit/post%20graduate%20work%20permit/',
              },
              {
                label: '加拿大 【LMIA 工作签证】',
                enLabel: 'LMIA-Based Work Permit',
                link: '/visa/other%20work%20permit/lmia%20work%20permit/',
              },
              {
                label: '加拿大 【ICT 工签】',
                enLabel: 'Intra-Company Transfer Work Permit (ICT Work Permit)',
                link: '/work-permit-for-intra-company-transfer/',
              },
              {
                label: '美国【旅游签证】',
                enLabel: 'U.S. Visitor Visa (B-2 Visa)',
                link: 'https://posts.starringglobal.com/%e7%be%8e%e5%9b%bd%e6%97%85%e6%b8%b8%e7%ad%be%e8%af%81%ef%bc%88b-2%ef%bc%89%e5%8a%9e%e7%90%86%e5%85%a8%e6%8c%87%e5%8d%97/',
              },
              {
                label: '日本【经营管理签证】',
                enLabel: 'Japanese Business Manager Visa',
                link: '/日本经营管理签证/',
              },
            ].map((item, index) => {
              return (
                <div
                  key={index}
                  style={{
                    backgroundImage: `url(/images/home/bannerBg${(index % 6) + 1}.png)`,
                  }}
                  className="box-border flex flex-col items-start justify-start rounded-lg bg-[#e6eaff] bg-cover bg-center bg-no-repeat p-[9px]"
                  onClick={() => {
                    const link = item.link;
                    if (link) {
                      if (link.startsWith('http') || link.startsWith('https')) {
                        window.open(link, '_blank');
                      } else {
                        window.open(`${process.env.NEXT_PUBLIC_WEBSITE}${link}`, '_blank');
                      }
                    }
                  }}
                >
                  <div className="mb-2 text-xs font-semibold tracking-tighter text-[#333333]">{item.label}</div>
                  <div className="text-[10px] leading-[12px] tracking-tighter text-[#666666]">{item.enLabel}</div>
                </div>
              );
            })}
            <div
              className="box-border flex min-h-16 items-center justify-center rounded-lg bg-[#e6eaff] p-[9px] text-xs text-[#333333]"
              onClick={() => router.push('/visa')}
            >
              {t('Home.seeMore')}
            </div>
          </div>
        </div>
      </div>
      {/* 星环移民 热点移民项目 */}
      <div className="mb-[2.34vw] box-border w-full px-[10.4vw] max-md:hidden">
        <h2 className="mb-[1.04vw] text-[2.392vw] font-semibold leading-[2.392vw] text-[#111111]">
          星环移民 热点移民项目
        </h2>
        <h2 className="mb-[1.924vw] text-[1.872vw] font-extrabold uppercase leading-[1.872vw] text-[#E2E2E2]">
          Hot Global Immigration Programs by Star Ring
        </h2>
      </div>
      <div className="mb-[7.8vw] box-border flex w-full flex-row items-start justify-center max-md:hidden">
        <div className="w-full">
          <div className="mb-[1.04vw] flex w-full flex-row justify-center">
            {/* 加拿大【法语人才移民】 */}
            <div
              className="mr-[1.04vw] box-border flex w-[39.208vw] cursor-pointer flex-row items-start justify-start rounded-[20px] bg-[#F8F1FF] px-[2.808vw] py-[2.34vw]"
              onClick={() => {
                window.open(
                  'https://posts.starringglobal.com/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e3%80%90%e6%b3%95%e8%af%ad%e4%ba%ba%e6%89%8d%e7%a7%bb%e6%b0%91%e3%80%91/',
                  '_blank',
                );
              }}
            >
              <Image
                src={'/images/home/businessIcon11.png'}
                width={80}
                height={80}
                className="mr-[2.392vw] size-[4.16vw]"
                alt="businessIcon"
              />
              <div className="flex-1">
                <h2 className="mb-[1.404vw] text-[1.872vw] font-medium leading-[1.872vw] text-[#28156F]">
                  {t('Home.businessCard11Title')}
                </h2>
                <h2 className="mb-[1.04vw] text-[1.04vw] font-normal leading-tight text-[#111111]">
                  {`Canadian French-Language Proficiency under Express Entry`}
                </h2>
                <h3 className="mb-[1.144vw] text-[1.04vw] font-medium leading-tight text-[#28156F]">
                  {currentLocale === 'en' ? (
                    <span>
                      The most cost-effective Express Entry pathway: low score requirement, no occupation restrictions
                      <br />A structured French language program to help you reach CLB 7 within 6 months
                    </span>
                  ) : (
                    <span>
                      最具性价比的EE途径：低分上岸、职业不限
                      <br />
                      系统法语课程助你6个月达到CLB7
                    </span>
                  )}
                </h3>
                <Image
                  src={'/images/home/leftArrow.png'}
                  width={27}
                  height={13}
                  className="h-auto w-[1.404vw]"
                  alt="arrowIcon"
                />
              </div>
            </div>
            {/* 美国【EB3雇主担保移民】 */}
            <Link
              href="/us-eb3"
              className="box-border flex w-[39.208vw] cursor-pointer flex-row items-start justify-start rounded-[20px] bg-[#F8F1FF] px-[2.808vw] py-[2.34vw]"
            >
              <Image
                src={'/images/home/businessIcon12.png'}
                width={80}
                height={80}
                className="mr-[2.392vw] size-[4.16vw]"
                alt="businessIcon"
              />
              <div className="flex-1">
                <h2 className="mb-[1.404vw] text-[1.872vw] font-medium leading-[1.872vw] text-[#0E572B]">
                  {t('Home.businessCard12Title')}
                </h2>
                <h2 className="mb-[1.04vw] text-[1.04vw] font-normal leading-tight text-[#111111]">
                  {`The U.S.Employment-Based Immigration: Third Preference EB-3`}
                </h2>
                <h3 className="mb-[1.144vw] text-[1.04vw] font-medium leading-tight text-[#0E572B]">
                  {t('Home.businessCard12Content')}
                </h3>
                <Image
                  src={'/images/home/leftArrow2.png'}
                  width={27}
                  height={13}
                  className="h-auto w-[1.404vw]"
                  alt="arrowIcon"
                />
              </div>
            </Link>
          </div>
          <div className="mb-[1.04vw] flex w-full flex-row items-center justify-center">
            <div className="flex flex-row flex-wrap justify-center">
              {/* 加拿大【留学转移民】 */}
              <div
                className="mb-[1.04vw] mr-[1.04vw] box-border flex w-[25.48vw] cursor-pointer flex-row items-center justify-start rounded-[20px] bg-[#FFEDFB] px-[1.456vw] py-[1.768vw]"
                onClick={() => {
                  window.open(
                    process.env.NEXT_PUBLIC_WEBSITE +
                      '/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%a0%94%e7%a9%b6%e7%94%9f%e7%95%99%e5%ad%a6%e8%bd%ac%e7%a7%bb%e6%b0%91%e5%85%a8%e6%94%bb%e7%95%a5/',
                    '_blank',
                  );
                }}
              >
                <Image
                  src={'/images/home/businessIcon1.png'}
                  width={66}
                  height={66}
                  className="mr-[1.352vw] size-[3.432vw]"
                  alt="businessIcon"
                />
                <div className="flex-1">
                  <h2 className="mb-[0.52vw] text-[1.096vw] font-medium leading-[1.196vw] text-[#111111]">
                    {t('Home.businessCard1Title')}
                  </h2>
                  <h2 className="mb-[0.832vw] text-[0.728vw] font-normal leading-[1.04vw] text-[#666666]">
                    {`Canadian Study Pathway to Permanent Residency Card`}
                  </h2>
                  <h3 className="text-[0.96vw] font-normal leading-tight text-[#111111] opacity-80">
                    {t('Home.businessCard1Content')}
                  </h3>
                </div>
              </div>
              {/* 加拿大【RCIP社区移民试点】 */}
              <div
                className="mb-[1.04vw] mr-[1.04vw] box-border flex w-[25.48vw] cursor-pointer flex-row items-center justify-start rounded-[20px] bg-[#FFF5EA] px-[1.456vw] py-[1.768vw]"
                onClick={() => {
                  window.open(
                    process.env.NEXT_PUBLIC_WEBSITE +
                      '/%E5%8A%A0%E6%8B%BF%E5%A4%A7%E9%98%BF%E7%9C%81rrs%E5%81%8F%E8%BF%9C%E5%9C%B0%E5%8C%BA%E9%9B%87%E4%B8%BB%E6%8B%85%E4%BF%9D%E7%A7%BB%E6%B0%91/',
                    '_blank',
                  );
                }}
              >
                <Image
                  src={'/images/home/businessIcon2.png'}
                  width={66}
                  height={66}
                  className="mr-[1.352vw] size-[3.432vw]"
                  alt="businessIcon"
                />
                <div className="flex-1">
                  <h2 className="mb-[0.52vw] text-[1.096vw] font-medium leading-[1.196vw] text-[#111111]">
                    {t('Home.businessCard2Title')}
                  </h2>
                  <h2 className="mb-[0.832vw] text-[0.728vw] font-normal leading-[1.04vw] text-[#666666]">
                    {`Canadian Rural Community Immigration Pilot`}
                  </h2>
                  <h3 className="text-[0.96vw] font-normal leading-tight text-[#111111] opacity-80">
                    {t('Home.businessCard2Content')}
                  </h3>
                </div>
              </div>
              {/* 安提瓜和巴布达【公民身份计划】 */}
              <div
                className="mb-[1.04vw] box-border flex w-[25.48vw] cursor-pointer flex-row items-center justify-start rounded-[20px] bg-[#FBEDFF] px-[1.456vw] py-[1.768vw]"
                onClick={() => {
                  window.open(
                    process.env.NEXT_PUBLIC_WEBSITE +
                      '/%e5%ae%89%e6%8f%90%e7%93%9c%e5%92%8c%e5%b7%b4%e5%b8%83%e8%be%be%e5%85%ac%e6%b0%91%e8%ba%ab%e4%bb%bd%e8%ae%a1%e5%88%92/',
                    '_blank',
                  );
                }}
              >
                <Image
                  src={'/images/home/businessIcon7.png'}
                  width={66}
                  height={66}
                  className="mr-[1.352vw] size-[3.432vw]"
                  alt="businessIcon"
                />
                <div className="flex-1">
                  <h2 className="mb-[0.52vw] text-[1.096vw] font-medium leading-[1.196vw] text-[#111111]">
                    {t('Home.businessCard7Title')}
                  </h2>
                  <h2 className="mb-[0.832vw] text-[0.728vw] font-normal leading-[1.04vw] text-[#666666]">
                    {`Antigua and Barbuda Citizenship by Investment Programme`}
                  </h2>
                  <h3 className="text-[0.96vw] font-normal leading-tight text-[#111111] opacity-80">
                    {t('Home.businessCard7Content')}
                  </h3>
                </div>
              </div>
              {/* 爱尔兰投资居留计划 */}
              <div
                className="mr-[1.04vw] box-border flex w-[25.48vw] cursor-pointer flex-row items-center justify-start rounded-[20px] bg-[#E4F8FA] px-[1.456vw] py-[1.768vw]"
                onClick={() => {
                  window.open(
                    process.env.NEXT_PUBLIC_WEBSITE +
                      '/%e7%88%b1%e5%b0%94%e5%85%b0%e6%8a%95%e8%b5%84%e5%b1%85%e7%95%99%e8%ae%a1%e5%88%92/',
                    '_blank',
                  );
                }}
              >
                <Image
                  src={'/images/home/businessIcon10.png'}
                  width={66}
                  height={66}
                  className="mr-[1.352vw] size-[3.432vw]"
                  alt="businessIcon"
                />
                <div className="flex-1">
                  <h2 className="mb-[0.52vw] text-[1.096vw] font-medium leading-[1.196vw] text-[#111111]">
                    {t('Home.businessCard10Title')}
                  </h2>
                  <h2 className="mb-[0.832vw] text-[0.728vw] font-normal leading-[1.04vw] text-[#666666]">
                    {`Irish Immigrant Investor Programme`}
                  </h2>
                  <h3 className="pr-[1.04vw] text-[0.96vw] font-normal leading-tight text-[#111111] opacity-80">
                    {t('Home.businessCard10Content')}
                  </h3>
                </div>
              </div>
              {/* 美国【EB5投资移民】 */}
              <div
                className="mr-[1.04vw] box-border flex w-[25.48vw] flex-row items-center justify-start rounded-[20px] bg-[#F6F1FF] px-[1.456vw] py-[1.768vw]"
                onClick={() => {
                  window.open(
                    process.env.NEXT_PUBLIC_WEBSITE + '/%e7%be%8e%e5%9b%bdeb5%e6%8a%95%e8%b5%84%e7%a7%bb%e6%b0%91/',
                    '_blank',
                  );
                }}
              >
                <Image
                  src={'/images/home/businessIcon3.png'}
                  width={66}
                  height={66}
                  className="mr-[1.352vw] size-[3.432vw]"
                  alt="businessIcon"
                />
                <div className="flex-1">
                  <h2 className="mb-[0.52vw] text-[1.096vw] font-medium leading-[1.196vw] text-[#111111]">
                    {t('Home.businessCard3Title')}
                  </h2>
                  <h2 className="mb-[0.832vw] text-[0.728vw] font-normal leading-[1.04vw] text-[#666666]">
                    {`The U.S.Employment-Based lmmigration: Third Preference EB-5
`}
                  </h2>
                  <h3 className="text-[0.96vw] font-normal leading-tight text-[#111111] opacity-80">
                    {t('Home.businessCard3Content')}
                  </h3>
                </div>
              </div>
              {/* 土耳其护照项目 */}
              <div
                className="box-border flex w-[25.48vw] cursor-pointer flex-row items-center justify-start rounded-[20px] bg-[#DAECFF] px-[1.456vw] py-[1.768vw]"
                onClick={() => {
                  window.open(
                    'https://posts.starringglobal.com/%E5%9C%A3%E5%9F%BA%E8%8C%A8pbo%E6%8D%90%E6%AC%BE%E7%A7%BB%E6%B0%91%E9%A1%B9%E7%9B%AE/',
                    '_blank',
                  );
                }}
              >
                <Image
                  src={'/images/home/businessIcon6.png'}
                  width={66}
                  height={66}
                  className="mr-[1.352vw] size-[3.432vw]"
                  alt="businessIcon"
                />
                <div className="flex-1">
                  <h2 className="mb-[0.52vw] text-[1.096vw] font-medium leading-[1.196vw] text-[#111111]">
                    {t('Home.businessCard6Title')}
                  </h2>
                  <h2 className="mb-[0.832vw] text-[0.728vw] font-normal leading-[1.04vw] text-[#666666]">
                    {`Saint Kitts and Nevis Citizenship Program`}
                  </h2>
                  <h3 className="text-[0.96vw] font-normal leading-tight text-[#111111] opacity-80">
                    <span>
                      离岸金融中心丨3-6个月获批
                      <br />
                      免签150+国家地区丨申请全程无需登录
                    </span>
                  </h3>
                </div>
              </div>
            </div>
          </div>
          <div className="box-border grid w-full grid-cols-4 justify-center gap-[1.04vw] px-[10.4vw]">
            {cards.map((card, index) => (
              <BusinessCard key={index} {...card} />
            ))}
          </div>
        </div>
      </div>
      {/* 星环移民 最新资讯动态 */}
      <LatestNews className="mb-[7.8vw] max-md:hidden" />
      {/* 成功案例 */}
      <SuccessCases successCasesList={successCasesList} />
      {/* 资质认证 */}
      <div className="mb-[7.8vw] box-border flex w-full flex-row items-start justify-center max-md:hidden">
        <div className="w-[79.508vw]">
          <div className="mb-8 h-[22.152vw] w-full bg-[#E8E8F0]">
            <Image
              src={'/images/home/authentication.jpg'}
              width={1529}
              height={426}
              className="h-full w-full object-cover"
              alt="authentication"
            />
          </div>
          <div className="flex w-full flex-row justify-center">
            <div className="flex w-1/2 flex-col items-center justify-center">
              <h5 className="mb-[35px] text-xl font-semibold leading-5 text-[#111111]">
                {t('Home.authenticationText1')}
              </h5>
              <div
                className="flex h-11 w-[180px] cursor-pointer flex-row items-center justify-center bg-[#242758] text-lg font-normal text-white"
                onClick={() => {
                  window.open('https://register.college-ic.ca/Pr_Profile_RCIC?ID=21648', '_blank');
                }}
              >
                {t('Home.goToDee')}
              </div>
            </div>
            <div className="flex w-1/2 flex-col items-center justify-center">
              <h5 className="mb-[35px] text-xl font-semibold leading-5 text-[#111111]">
                {t('Home.authenticationText2')}
              </h5>
              <div
                className="flex h-11 w-[180px] cursor-pointer flex-row items-center justify-center bg-[#242758] text-lg font-normal text-white"
                onClick={() => {
                  window.open(
                    'https://www.bbb.org/ca/bc/richmond/profile/immigration-consultant/star-ring-immigration-consulting-inc-0037-2409385',
                    '_blank',
                  );
                }}
              >
                {t('Home.goToDee')}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 资质认证 h5 */}
      <div className="mb-[38px] box-border w-full px-[15px] md:hidden">
        <div className="mb-[22px] w-full text-lg font-semibold leading-[18px] text-[#111111]">
          {t('Home.certification')}
        </div>
        {new Array(2).fill(0).map((item, index) => (
          <div key={index} className="mb-[27px] w-full last:mb-0">
            <Image
              src={`/images/home/certification${index + 1}.png`}
              width={690}
              height={225}
              className="mb-[15px] h-[112px] w-full rounded-xl object-cover"
              alt="authentication"
            />
            <h5 className="text-center text-sm font-semibold text-[#111111]">
              {t(`Home.authenticationText${index + 1}` as any)}
            </h5>
          </div>
        ))}
      </div>
      {/* 成功案例 h5 */}
      <div className="mb-[48px] box-border w-full px-[15px] md:hidden">
        <div className="mb-[22px] w-full text-lg font-semibold leading-[18px] text-[#111111]">
          {t('Home.successCases')}
        </div>
        {successCasesList.map((item, index) => (
          <Link
            href={item.link}
            key={index}
            style={{ boxShadow: '0px 4px 6px 0px rgba(42,43,69,0.24)' }}
            className="mb-[15px] w-full cursor-pointer rounded-xl bg-white last:mb-0"
          >
            <div
              style={{ backgroundImage: `url(${item.image_1})` }}
              className="h-[170px] w-full rounded-xl bg-slate-600 bg-cover bg-top bg-no-repeat"
            ></div>
            <div className="box-border flex min-h-[96px] w-full flex-col items-start justify-between rounded-b-[20px] bg-white px-3 pb-[13px] pt-[22px]">
              <div className="mb-3 text-sm leading-[14px] text-[#111111]">{item.title}</div>
              <div className="flex w-full flex-row items-center justify-start">
                {[item.tag].map((tagItem, index) => (
                  <div
                    key={index}
                    className="mr-[5px] rounded-[10px] bg-[#F2F2F8] px-[14px] py-2 text-sm leading-[14px] text-[#111111] last:mr-0"
                  >
                    {tagItem}
                  </div>
                ))}
              </div>
            </div>
          </Link>
        ))}
        <Link href="/successful-cases" className="mb-11 mt-7 flex w-full flex-row items-center justify-center">
          <div
            className="flex h-[35px] w-[150px] cursor-pointer flex-row items-center justify-center rounded-md bg-[#FFAF3A] text-sm text-[#111111]"
            // onClick={() => {
            //   window.open(process.env.NEXT_PUBLIC_WEBSITE + '/all_successful_cases/', '_blank');
            // }}
          >
            {t('Home.moreCases')}
          </div>
        </Link>
      </div>
      {/* 星环移民 客户评价 */}
      <div className="box-border min-h-[56.16vw] w-full bg-[#F0F0FE] pb-[2.236vw] pt-[3.9vw] max-md:hidden">
        <div className="mb-12 box-border flex w-full flex-row items-start justify-between px-[10.192vw]">
          <div>
            <h2 className="mb-5 text-[46px] font-semibold leading-[46px] text-[#111111]">
              {t('Home.customerReviews')}
            </h2>
            <h2 className="mb-[26px] text-[36px] font-extrabold uppercase leading-[36px] text-[#E2E2E2]">
              {t('Home.customerReviewsEnglishText')}
            </h2>
            <div className="text-[30px] font-normal leading-[30px] text-[#ED8000]">{t('Home.googleRating')}</div>
          </div>
          <div
            className="flex h-11 w-[180px] cursor-pointer flex-row items-center justify-center bg-[#282849] text-lg font-normal text-white"
            onClick={() => {
              window.open(
                'https://www.google.com/maps/place/%E6%98%9F%E7%8E%AF%E7%A7%BB%E6%B0%91+Starring+Immigration/@49.1704771,-123.1359827,17z/data=!4m8!3m7!1s0x548675e631e16273:0x3c1efb47b80b0698!8m2!3d49.1704771!4d-123.1359827!9m1!1b1!16s%2Fg%2F11j0s1hn5l?entry=ttu&g_ep=EgoyMDI1MDEwNi4xIKXMDSoASAFQAw%3D%3D',
                '_blank',
              );
            }}
          >
            {t('Home.seeMore')}
          </div>
        </div>
        <div className="w-full">
          <Swiper
            className="homePageCustomerReviewsSwiper min-h-[34.112vw]"
            effect={'coverflow'}
            grabCursor={true}
            centeredSlides={true}
            slidesPerView={'auto'}
            coverflowEffect={{
              rotate: 50,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true,
            }}
            navigation={{
              enabled: true,
              prevEl: '.homePageCustomerReviewsSwiperButtonPrev',
              nextEl: '.homePageCustomerReviewsSwiperButtonNext',
            }}
            modules={[EffectCoverflow, Navigation]}
          >
            {commentList.map((commentItem, index) => {
              return (
                <SwiperSlide key={index} className="min-h-[34.112vw] w-[47.32vw] bg-white">
                  <div className="min-h-[34.112vw] w-[47.32vw] bg-white">
                    <div
                      style={{ backgroundImage: `url(${commentItem.image})` }}
                      className="relative h-[17.89vw] w-full bg-cover bg-center bg-no-repeat"
                    >
                      <div
                        style={{ background: 'linear-gradient(transparent, white)' }}
                        className="absolute bottom-0 left-0 right-0 h-[2.34vw] w-full"
                      ></div>
                      {/* <div className="absolute bottom-[-2.08vw] left-[50%] flex w-full translate-x-[-50%] flex-col items-center justify-center">
                        <div className="mb-[0.78vw] size-[4.16vw] rounded-full">
                          <Image
                            src={commentItem.avatar}
                            width={80}
                            height={80}
                            className="size-[4.16vw] rounded-full"
                            alt="avatar"
                          />
                        </div>
                        <div className="text-[0.884vw] uppercase leading-[0.884vw] text-[#333333]">
                          {commentItem?.userName}
                        </div>
                      </div> */}
                      <Image
                        src={'/images/home/reviewsIcon.png'}
                        width={26}
                        height={21}
                        className="absolute bottom-[-2.08vw] left-[0.728vw] h-auto w-[1.352vw]"
                        alt="reviewsIcon"
                      />
                    </div>
                    <div className="box-border w-full px-[2.08vw] pb-[1.82vw] pt-[2.284vw]">
                      <div
                        style={{
                          overflow: 'hidden',
                          display: '-webkit-box',
                          textOverflow: 'ellipsis',
                          WebkitLineClamp: '4',
                          WebkitBoxOrient: 'vertical',
                        }}
                        className="mb-[1.68vw] h-[7.5vw] text-[1.04vw] leading-[1.872vw] text-[#333333]"
                      >
                        {commentItem?.content}
                      </div>
                      <div className="mb-[0.48vw] text-[0.884vw] uppercase leading-[0.884vw] text-[#333333]">
                        {t('Home.from')}: {commentItem?.userName}
                      </div>
                      <div className="flex w-full flex-row items-center justify-between">
                        <div className="flex flex-row items-center justify-start">
                          {new Array(5).fill(0).map((_, index) => {
                            return (
                              <Image
                                key={index}
                                src={'/images/home/star.png'}
                                width={30}
                                height={30}
                                className="mr-[0.56vw] size-[1.56vw] last:mr-0"
                                alt="reviewsStar"
                              />
                            );
                          })}
                        </div>
                        <Link
                          href={commentItem?.link}
                          target="_blank"
                          className="text-lg font-normal leading-[18px] text-[#181E83]"
                        >
                          {t('Home.viewThisReviewOnGoogle')}
                        </Link>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
            <div className="mt-[1.404vw] flex w-full flex-row items-center justify-center">
              <Image
                src={'/images/home/roundLeftArrow.png'}
                width={83}
                height={84}
                className="homePageCustomerReviewsSwiperButtonPrev h-auto w-[4.316vw] cursor-pointer"
                alt="roundLeftArrow"
              />
              <Image
                src={'/images/home/roundRightArrow.png'}
                width={83}
                height={84}
                className="homePageCustomerReviewsSwiperButtonNext h-auto w-[4.316vw] cursor-pointer"
                alt="roundRightArrow"
              />
            </div>
          </Swiper>
        </div>
      </div>
      {/* 星环移民 客户评价 - h5 */}
      <div className="w-full md:hidden">
        <div className="box-border flex w-full items-center justify-between px-[15px] text-lg font-semibold leading-[18px] text-[#111111]">
          {t('Home.customerReviews')}
          <span
            className="text-sm leading-[14px]"
            onClick={() => {
              window.open(
                'https://www.google.com/maps/place/%E6%98%9F%E7%8E%AF%E7%A7%BB%E6%B0%91+Starring+Immigration/@49.1704771,-123.1359827,17z/data=!4m8!3m7!1s0x548675e631e16273:0x3c1efb47b80b0698!8m2!3d49.1704771!4d-123.1359827!9m1!1b1!16s%2Fg%2F11j0s1hn5l?entry=ttu&g_ep=EgoyMDI1MDEwNi4xIKXMDSoASAFQAw%3D%3D',
                '_blank',
              );
            }}
          >
            {t('Home.seeMore')}
          </span>
        </div>
        <div className="mt-1 px-[15px] text-[16px] text-[#ED8000]">{t('Home.googleRating')}</div>
        <div
          id="h5HomePageCustomerReviews"
          className="box-border flex w-full flex-row items-center justify-start gap-[15px] overflow-x-auto px-[15px] py-1 pb-[43px] pt-[30px]"
        >
          <Link
            href={'https://maps.app.goo.gl/DLGskJUjJwLUZkC88'}
            target="_blank"
            style={{ boxShadow: '-1px 7px 17px 1px rgba(42,42,42,0.17)' }}
            className="box-border flex h-[194px] w-[303px] flex-shrink-0 flex-col items-start justify-between bg-white px-[13px] pb-[15px] pt-[22px]"
          >
            <Image src={'/images/home/review.png'} width={460} height={207} className="h-full w-full" alt="review" />
          </Link>
          {commentList.map((item, index) => (
            <Link
              href={item.link}
              target="_blank"
              key={index}
              style={{ boxShadow: '-1px 7px 17px 1px rgba(42,42,42,0.17)' }}
              className="box-border flex min-h-[190px] w-[303px] flex-shrink-0 flex-col items-start justify-between bg-white px-[13px] pb-[15px] pt-[22px]"
            >
              <div className="mb-2 flex w-full flex-row items-start justify-start">
                <Image
                  src={'/images/home/reviewsIcon.png'}
                  width={26}
                  height={21}
                  className="mr-[13px] h-auto w-[13px]"
                  alt="reviewsIcon"
                />
                <div className="flex-1">
                  <div
                    style={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      textOverflow: 'ellipsis',
                      WebkitLineClamp: '7',
                      WebkitBoxOrient: 'vertical',
                    }}
                    className="mt-1 h-[120px] w-full text-xs leading-[17px] text-[#333333]"
                  >
                    {item.content}
                  </div>
                </div>
              </div>
              <div className="box-border flex w-full flex-row items-center justify-between pl-4 pr-1">
                <div className="flex flex-row items-center justify-between">
                  <Image
                    src={item.avatar}
                    width={25}
                    height={25}
                    className="mr-[9px] size-[25px] rounded-full"
                    alt="avatar"
                  />
                  <div className="text-sm text-[#333333]">{item.userName}</div>
                </div>
                <div className="flex flex-row items-center justify-start">
                  {new Array(5).fill(0).map((_, index) => (
                    <Image
                      key={index}
                      src={'/images/home/star.png'}
                      width={30}
                      height={30}
                      className="mr-1 size-4 last:mr-0"
                      alt="reviewsStar"
                    />
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      {/* 常见问题 */}
      <div className="mb-[7.8vw] box-border w-full px-[10.192vw] pt-[7.8vw] max-md:mb-[43px] max-md:px-[15px] max-md:pt-0">
        <div className="mb-5 text-[46px] font-semibold leading-[46px] text-[#111111] max-md:mb-6 max-md:text-lg max-md:leading-[18px]">
          {t('Home.faq')}
        </div>
        <div className="mb-20 text-[36px] font-extrabold uppercase leading-[36px] text-[#E2E2E2] max-md:hidden">
          {t('Home.commonProblem')}
        </div>
        <FaqSection items={faqList.map((item) => ({ question: item.title, answer: item.answer }))} />
      </div>
      {/* 关注我们 */}
      <FollowUs />
      <div className="fixed bottom-[26px] right-0 flex w-full flex-row items-center justify-center md:hidden">
        <div className="flex h-[60px] w-[92vw] flex-row items-center justify-between rounded-[60px] bg-[#212136fa]">
          <div
            className="box-border flex h-full w-1/3 flex-row items-center justify-center px-3"
            onClick={() => {
              setShowContactCustomerService(true);
            }}
          >
            <Image
              src={'/images/home/icon_user.png'}
              width={15}
              height={15}
              className="mr-[3px] size-[15px]"
              alt="icon_user"
            />
            <span className={`${currentLocale === 'zh' ? 'text-sm' : 'w-14 text-xs'} text-center text-[#F3F3FF]`}>
              {t('Home.contactCustomerService')}
            </span>
          </div>
          <div
            className="box-border flex h-full w-1/3 flex-row items-center justify-center px-3"
            onClick={() => {
              window.open('https://posts.starringglobal.com/%E5%BF%AB%E9%80%9F%E8%AF%84%E4%BC%B0%E8%A1%A8/', '_blank');
            }}
          >
            <Image
              src={'/images/home/icon_edit.png'}
              width={15}
              height={15}
              className="mr-[3px] size-[15px]"
              alt="icon_user"
            />
            <span className={`${currentLocale === 'zh' ? 'text-sm' : 'text-xs'} text-center text-[#F3F3FF]`}>
              {t('Home.fillInRequirements')}
            </span>
          </div>
          {/* 拨打电话 */}
          <div
            className="box-border flex h-full w-1/3 flex-row items-center justify-center px-3"
            onClick={() => {
              setShowPhoneDialog(true);
            }}
          >
            <Image
              src={'/images/home/icon_phone.png'}
              width={15}
              height={15}
              className="mr-[3px] size-[15px]"
              alt="icon_user"
            />
            <span className={`${currentLocale === 'zh' ? 'text-sm' : 'text-xs'} text-center text-[#F3F3FF]`}>
              {t('Home.makeAPhoneCall')}
            </span>
          </div>
        </div>
      </div>
      {showContactCustomerService && (
        <div className="fixed bottom-0 right-0 box-border flex h-[120px] w-full flex-row items-start justify-center bg-[#212136fa] pt-5 md:hidden">
          <div>
            <div className="mb-[26px] text-sm font-medium leading-[14px] text-[#F3F3FF]">
              {t('Home.tailoredForYou')}
            </div>
            <div
              className="flex h-[33px] w-[100px] flex-row items-center justify-center bg-[#FFB83C] text-[22px] text-xs"
              onClick={() => {
                window.open(
                  'https://posts.starringglobal.com/%E5%BF%AB%E9%80%9F%E8%AF%84%E4%BC%B0%E8%A1%A8/',
                  '_blank',
                );
              }}
            >
              {t('AddCustomerService.buttonText')}
            </div>
          </div>
          <div className="mx-[26px] mt-[18px] h-[45px] w-[1px] bg-[#F3F3FF] opacity-60"></div>
          <div>
            <div className="mb-[18px] text-sm font-medium leading-[14px] text-[#F3F3FF]">
              {t('Immigration.consultCustomerService')}
            </div>
            <div className="mb-[10px] flex flex-row items-center justify-start">
              <div className="mr-6 text-xs text-[#F3F3FF]">{t('AddCustomerService.wechat')}Starringca002</div>
              <div
                className="h-[21px] flex-row items-center justify-center rounded-[21px] border border-[#E2E2FF] px-[11px] text-xs leading-5 text-[#F3F3FF]"
                onClick={() => {
                  if (showCopySuccess) return;
                  copy('Starringca002', {
                    debug: true,
                    message: t('Home.copySuccessful'),
                  });
                  setShowCopySuccess(true);
                  setTimeout(() => {
                    setShowCopySuccess(false);
                  }, 2000);
                }}
              >
                {showCopySuccess ? <Check className="size-5" /> : t('Home.copy')}
              </div>
            </div>
            <div className="flex flex-row items-center justify-start">
              <div className="mr-[11px] text-xs text-[#F3F3FF]">{t('Home.weChatSupportsLongPressRecognition')}</div>
              <div
                className="flex h-[21px] flex-row items-center justify-center rounded-[21px] border border-[#E2E2FF] px-[8px] text-xs text-[#F3F3FF]"
                onClick={() => {
                  setShowQRCode(true);
                  setShowContactCustomerService(false);
                }}
              >
                {t('Home.displayQRCode')}
              </div>
            </div>
          </div>
          <div
            className="absolute right-1 top-1 flex size-8 cursor-pointer flex-row items-center justify-center text-2xl text-white"
            onClick={() => setShowContactCustomerService(false)}
          >
            <Image src={'/images/home/close.png'} width={16} height={16} className="size-4" alt="close" />
          </div>
        </div>
      )}
      {showQRCode && (
        <div className="fixed bottom-0 right-0 box-border flex h-[199px] w-full flex-row items-end justify-center bg-[#212136fa] py-5 md:hidden">
          <div className="w-[114px]">
            <div className="mb-[15px] text-center text-sm leading-[14px] text-[#F3F3FF]">Jackson</div>
            {
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={'/images/footer/wechatNumber1.png'}
                width={114}
                height={115}
                className="w-[114px]"
                alt="wechatNumber"
              />
            }
          </div>
          <div className="mx-[34px] mb-[35px] h-[45px] w-[1px] bg-[#F3F3FF] opacity-60"></div>
          <div className="w-[114px]">
            <div className="mb-[15px] text-center text-sm leading-[14px] text-[#F3F3FF]">June Lee</div>
            {
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={'/images/footer/wechatNumber2.png'}
                width={114}
                height={115}
                className="w-[114px]"
                alt="wechatNumber"
              />
            }
          </div>
          <div
            className="absolute right-1 top-1 flex size-8 cursor-pointer flex-row items-center justify-center text-2xl text-white"
            onClick={() => setShowQRCode(false)}
          >
            <Image src={'/images/home/close.png'} width={16} height={16} className="size-4" alt="close" />
          </div>
        </div>
      )}
      {showH5QRCode && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 md:hidden"
          onClick={() => setShowH5QRCode(null)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            {
              // eslint-disable-next-line @next/next/no-img-element
              <img src={showH5QRCode} width={250} height={250} className="size-[250px]" alt="qrcode" />
            }
            {/* <div
              className="absolute -right-3 -top-3 flex size-8 cursor-pointer items-center justify-center rounded-full bg-white"
              onClick={() => setShowH5QRCode(null)}
            >
              <Image src={'/images/home/close.png'} width={16} height={16} className="size-4" alt="close" />
            </div> */}
          </div>
        </div>
      )}
      {/* 电话号码弹窗 */}
      {showPhoneDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 md:hidden"
          onClick={() => setShowPhoneDialog(false)}
        >
          <div
            className="relative mx-4 w-full max-w-[345px] rounded-2xl bg-white px-6 pb-6 pt-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 关闭按钮 */}
            <div
              className="absolute right-3 top-3 flex size-8 cursor-pointer items-center justify-center"
              onClick={() => setShowPhoneDialog(false)}
            >
              <Image src={'/images/home/close.png'} width={16} height={16} className="size-4" alt="close" />
            </div>

            {/* 北美区域 */}
            <div className="mb-6">
              <div className="mb-3 text-sm font-medium text-[#111111]">北美（温哥华总部）</div>
              <a
                href="tel:+16043706618"
                className="flex h-[50px] w-full items-center justify-center rounded-full border-2 border-[#111111] text-lg font-medium text-[#111111]"
              >
                +1-604 370 6618
              </a>
            </div>

            {/* 中国区域 */}
            <div className="mb-6">
              <div className="mb-3 text-sm font-medium text-[#111111]">中国（上海分部）</div>
              <a
                href="tel:+862152988291"
                className="flex h-[50px] w-full items-center justify-center rounded-full border-2 border-[#111111] text-lg font-medium text-[#111111]"
              >
                +86-21-5298-8291
              </a>
            </div>

            {/* 关闭按钮 */}
            <div
              className="flex h-[44px] w-full cursor-pointer items-center justify-center rounded-full bg-white text-base font-medium text-[#111111]"
              style={{ border: '1px solid #111111' }}
              onClick={() => setShowPhoneDialog(false)}
            >
              关闭
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeContent;
